<template>
  <div class="row">
    <language-selector />

    <div class="col-12 mb-3">
      <div class="search">
        <input v-model="searchQuery" class="form-control" type="text" placeholder="search..." @input="runSearch">
        <button class="btn btn-outline-primary btn-stop-search" @click="stopSearch">
          <fa :icon="['fas', 'times']" />
        </button>
      </div>
    </div>

    <div class="col-12">
      <card title="List of Pages">
        <div class="row mb-5">
          <div class="col-12">
            <router-link
              class="btn btn-primary"
              tag="button"
              to="/pages/new"
            >
              Add new page
            </router-link>
          </div>
        </div>

        <v-table
          :columns="[
            { label: 'Name', field: 'name' },
            { label: 'Heading', field: 'heading.en' },
            { label: 'Description', field: 'description.en' },
            { label: 'Type', field: 'type' },
            { label: 'Sections', field: 'sections' },
          ]"
          :rows="displayList"
          :show-active-toggle="false"
          @edit="editPage($event._id)"
          @delete="deletePage($event._id)"
        />
      </card>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import { FETCH_NAVIGATION, FETCH_PAGES, PAGE_DELETE } from '@/store/actions.type'
import Swal from 'sweetalert2'
import VTable from '@/components/VTable'
import _ from 'lodash'

export default {
  name: "Pages",

  components: { VTable },

  data() {
    return {
      isShowingSearchResults: false,
      searchQuery: '',
      searchResults: []
    }
  },

  computed: {
    ...mapGetters([
      'pages',
      'navigation',
      'activeLanguage'
    ]),

    displayList() {
      if (this.isShowingSearchResults) {
        return this.searchResults
      } else {
        return this.pagesWithSections
      }
    },
    
    pagesWithSections () {
      let pagesWithSections = []
      this.pages.forEach(page => {
        pagesWithSections.push({ 
          ...page,
          sections: this.getPageSections(page._id).toString()
        })
      })

      return pagesWithSections
    }
  },

  beforeMount() {
    this.$store.dispatch(FETCH_PAGES)
      .then(res => {
        if (_.isEmpty(res)) {
          this.$notify({
            message: 'No pages found',
            horizontalAlign: 'center',
            verticalAlign: 'top',
            type: 'warning'
          })
        }
      })

    this.$store.dispatch(FETCH_NAVIGATION)
      .then(res => {
        if (res !== null && !_.isEmpty(this.navigation.categories)) {
          this.selectedTabId = this.navigation.categories[0].id
        } else {
          this.$notify({
            message: 'No navigation items found',
            horizontalAlign: 'center',
            verticalAlign: 'top',
            type: 'warning'
          });
        }
      })
      .catch(err => {
        this.$notify({
          message: 'Error - ' + err,
          horizontalAlign: 'center',
          verticalAlign: 'top',
          type: 'danger'
        });
      });
  },

  mounted() {
    this.$nextTick(() => {
      window.addEventListener('keyup', (e) => {
        if(e.keyCode === 27) {
          this.stopSearch()
        }
      })
    })
  },

  methods: {
    deletePage(id) {
      Swal.fire({
        title: 'Are you sure?',
        text: 'You won\'t be able to revert this!',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      }).then((result) => {
        if (result.value) {
          this.$store.dispatch(PAGE_DELETE, id)
            .then(() => {
                Swal.fire(
                  'Deleted!',
                  'The page has been deleted.',
                  'success'
                )

                this.$store.dispatch(FETCH_PAGES)
            })
        }
      })
    },

    editPage (id) {
      this.$router.push(`/pages/${id}`)
    },

    getPageSections (pageId) {
      let usedBy = []

      if (this.navigation.categories) {
        this.navigation.categories.forEach(category => {
          category.children.forEach(baseItem => {
            if (baseItem.pageId === pageId) {
              if (baseItem.label[this.activeLanguage.code] !== undefined) {
                usedBy.push(baseItem.label[this.activeLanguage.code])
              }
            }

            baseItem.children.forEach(childItem => {
              if (childItem.pageId === pageId) {
                if (childItem.label[this.activeLanguage.code] !== undefined) {
                  usedBy.push(childItem.label[this.activeLanguage.code])
                }
              }
            })
          })
        })
      }

      return usedBy
    },

    runSearch () {
      if (this.searchQuery.length < 2) {
        this.isShowingSearchResults = false
        return null
      }

      let results = _.cloneDeep(this.pagesWithSections)
      this.searchResults = []

      let keys = []
      let lang = this.activeLanguage ? this.activeLanguage.code : null

      if (lang) {
        keys = [`heading.${lang}`, `description.${lang}`]
      } else {
        keys = ['heading', 'description']
      }

      this.$search(
        this.searchQuery, 
        results, 
        { 
          keys: keys, 
          includeMatches: true, 
          minMatchCharLength: 2,
          distance: 600,
          findAllMatches: true
        }
      )
      .then(result => {
        result.forEach((element) => {
          let output = ''

          element.matches.forEach((match) => {
            const matchedString = match.value
            
            match.indices.forEach((matchPos, index) => {
              const tagStart = '<strong>'
              const tagEnd = '</strong>'
              let tagsLength = tagStart.length + tagEnd.length

              // for every loop add the string length of the added tags
              let start = matchPos[0] + (index * tagsLength) 
              let end = matchPos[1] + (index * tagsLength) + 1
  
              if (index === 0) {
                output = matchedString
              }
              
              output = output.substring(0, end) + tagEnd + output.substring(end)
              output = output.substring(0, start) + tagStart + output.substring(start)
            })

            let matchKeyArray = match.key.split('.')

            if (matchKeyArray.length === 1) {
              element.item[matchKeyArray[0]] = output
            } else if (matchKeyArray.length === 2) {
              element.item[matchKeyArray[0]][matchKeyArray[1]] = output
            }

            this.searchResults.push(element.item)
          })
        })
        
        // remove any duplicates (caused by finding matches in both heading and description)
        this.searchResults = _.uniq(this.searchResults, '_id');
        this.isShowingSearchResults = true
      })
    },

    stopSearch () {
      this.isShowingSearchResults = false
      this.searchQuery = ''
    }
  },

  beforeDestroy () {
    window.removeEventListener('keyup', function() {})
  }
}
</script>
<style lang="scss" scoped>

.search {
  position: relative;

  .btn-stop-search {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-color: transparent;
    font-size: 12px;
    height: 100%;
    position: absolute;
    right: 0;
    top: 0;
  }
}
</style>